// TestYourKnowledgeQuiz.jsx
import React, { useState, useEffect, useReducer } from "react";
import { Card, CardBody } from "../card/card.jsx";
import QuizNavigation from "../QuizNavigation/QuizNavigation.js";
import NavigationButtons from "../NavigationButtons/NavigationButtons.js";
import ProgressOverview from "../ProgressOverview/ProgressOverview.js";
import VoiceoverCard from "../VoiceoverCard/VoiceoverCard.js";
import TestYourKnowledgeItemCard from "../TestYourKnowledgeItemCard/TestYourKnowledgeItemCard.js"; // adjust import path as needed
import useAutoSaveQuiz from "../../hooks/useAutoSave.js";
import useArrowNavigation from "../../hooks/useArrowNavigation.js";
import useVoiceGenerator from "../../hooks/useVoiceGenerator.js";
import quizReducer from "../../reducers/quiz.js";
import { initTestYourKnowledgeReducer } from "../../reducers/initial.js";

const TestYourKnowledgeQuiz = ({ quizId, onCreateQuiz, channel, backendQuizData }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [quizStructure, dispatch] = useReducer(quizReducer, initTestYourKnowledgeReducer);

  const generateVoice = useVoiceGenerator(channel.voiceId);

  // Load backend quiz data if available
  useEffect(() => {
    if (backendQuizData) {
      dispatch({ type: "LOAD_QUIZ", payload: backendQuizData });
      setIsLoaded(true);
    }
  }, [backendQuizData]);

  // Arrow key navigation
  useArrowNavigation(
    () => setCurrentIndex((prev) => Math.max(0, prev - 1)),
    () => setCurrentIndex((prev) => Math.min(quizStructure.items.length, prev + 1)),
    [quizStructure.items.length, isLoaded]
  );

  useAutoSaveQuiz(quizId, isLoaded ? quizStructure : null);

  // A helper function to dispatch updates (new actions are used by the TestYourKnowledgeItemCard)
  const updateField = (index, actionType, payload) => {
    dispatch({ type: actionType, payload: { index, ...payload } });
  };

  const updatePreImageField = (index, field, value, isImages = false) => {
    const actionType = field === "prompt" ? "UPDATE_PREIMAGE_PROMPT" :
      field === "query" ? "UPDATE_PREIMAGE_QUERY" :
      isImages ? "UPDATE_PREIMAGE_IMAGES" : "UPDATE_PREIMAGE_SELECTION";
    dispatch({ type: actionType, payload: { index, [field]: value } });
  };

  const updatePostImageField = (index, field, value, isImages = false) => {
    const actionType = field === "prompt" ? "UPDATE_POSTIMAGE_PROMPT" :
      field === "query" ? "UPDATE_POSTIMAGE_QUERY" :
      isImages ? "UPDATE_POSTIMAGE_IMAGES" : "UPDATE_POSTIMAGE_SELECTION";
    dispatch({ type: actionType, payload: { index, [field]: value } });
  };

  const generateAnswerVoice = async (index) => {
    try {
      const voice = await generateVoice(quizStructure.items[index].answer.text);
      dispatch({ type: "UPDATE_ANSWER_VOICE", payload: { index, voice } });
      return voice;
    } catch (error) {
      console.error("Error generating answer voice:", error);
      throw error;
    }
  };

  // Disable create quiz button if required fields are missing
  const shouldDisableCreateButton = () => {
    if (!quizStructure?.introvoice?.voice) return true;
    return quizStructure.items.some(
      (item) =>
        !item.question.text ||
        !item.answer.voice ||
        !item.option1.text ||
        !item.option2.text ||
        !item.option3.text ||
        !item.preImage.selection ||
        !item.postImage.selection
    );
  };

  const isItemComplete = (item) => {
    return (
      item.voice?.voice &&
      item.option1?.selection &&
      item.option2?.selection &&
      item.option3?.selection &&
      item.answer?.voice &&
      item.preImage?.selection &&
      item.postImage?.selection
    );
  };

  const handleGenerateIntroVoice = async () => {
    try {
      const voice = await generateVoice(quizStructure.introvoice.text);
      dispatch({ type: "UPDATE_INTRO_VOICE", payload: { voice } });
      return voice;
    } catch (error) {
      console.error("Error generating intro voice:", error);
      throw error;
    }
  };

  const handleFinalCreateQuiz = () => {
    // Build final structure according to schema.
    const finalStructure = {
      title: quizStructure.title,
      introvoice: quizStructure.introvoice,
      items: quizStructure.items.map((item) => ({
        question: item.question,
        option1: item.option1,
        option2: item.option2,
        option3: item.option3,
        answer: item.answer,
        preImage: item.preImage,
        postImage: item.postImage,
      })),
    };
    onCreateQuiz(finalStructure);
  };

  return (
    <>
      <ProgressOverview
        quizStructure={quizStructure}
        currentIndex={currentIndex}
        onNavigate={setCurrentIndex}
        isItemComplete={isItemComplete}
      />

      {currentIndex === 0 && (
        <Card>
          <CardBody>
            <NavigationButtons
              onPrevious={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
              onNext={() => setCurrentIndex((prev) => Math.min(quizStructure.items.length, prev + 1))}
              disablePrevious={currentIndex === 0}
              disableNext={quizStructure.items.length === 0 ? true : currentIndex === quizStructure.items.length}
            />
            <div className="mb-4">
              <VoiceoverCard
                label="Intro Voiceover"
                voiceover={quizStructure.introvoice}
                onTextChange={(text) =>
                  updateField(null, "UPDATE_INTRO_VOICE_TEXT", { text })
                }
                onGenerateVoice={handleGenerateIntroVoice}
                shortcutModifier="alt"
              />
            </div>
          </CardBody>
        </Card>
      )}

      {currentIndex > 0 && (
        <TestYourKnowledgeItemCard
          index={currentIndex - 1}
          currentIndex={currentIndex}
          item={quizStructure.items[currentIndex - 1]}
          updateField={updateField}
          quizStructure={quizStructure}
          setCurrentIndex={setCurrentIndex}
          generateAnswerVoice={generateAnswerVoice}
          updatePreImageField={updatePreImageField}
          updatePostImageField={updatePostImageField}
          shortcutModifier="alt"
        />
      )}

      <Card>
        <CardBody>
          <button
            className="btn btn-success w-100"
            disabled={shouldDisableCreateButton()}
            onClick={handleFinalCreateQuiz}
          >
            Create Quiz
          </button>
        </CardBody>
      </Card>
    </>
  );
};

export default TestYourKnowledgeQuiz;
